import { toast } from './toast';

interface ErrorOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  customMessage?: string;
  silent?: boolean;
  retryCallback?: () => void;
}

export const handleError = (error: unknown, options: ErrorOptions = {}) => {
  const { 
    showToast = true, 
    logToConsole = true,
    customMessage,
    silent = false,
    retryCallback
  } = options;
  
  // Determine the error message
  let errorMessage: string;
  
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = 'An unknown error occurred';
  }
  
  // Check if this is a device-related error
  const isDeviceError = isMediaDeviceError(error);
  
  // Show toast if requested
  if (showToast && !silent) {
    if (isDeviceError) {
      // Use specialized device error toast for media device errors
      toast.device(customMessage || "Camera or microphone access error", {
        description: "Please check your device permissions and try again",
        action: retryCallback ? {
          label: "Retry",
          onClick: retryCallback
        } : undefined,
        duration: 10000 // Longer duration for device errors
      });
    } else if (retryCallback) {
      // Use toast structure that matches the sonner API
      toast.error(customMessage || errorMessage, {
        description: 'Click retry to try again',
        action: {
          label: "Retry",
          onClick: retryCallback
        },
        duration: 8000
      });
    } else {
      toast.error(customMessage || errorMessage, {
        duration: 5000 // Slightly longer duration for error messages
      });
    }
  }
  
  // Log to console if requested
  if (logToConsole) {
    console.error('Error:', error);
  }
  
  // Return the error message for further handling if needed
  return errorMessage;
};

export const isMediaDeviceError = (error: unknown): boolean => {
  if (error instanceof Error) {
    // Expanded detection for more device error types
    return (
      error.name === 'NotAllowedError' ||
      error.name === 'NotFoundError' ||
      error.name === 'NotReadableError' ||
      error.name === 'OverconstrainedError' ||
      error.name === 'AbortError' ||
      error.name === 'MediaDeviceFailedError' ||
      error.name === 'DevicesNotFoundError' ||
      error.name === 'PermissionDeniedError' ||
      error.message.includes('camera') ||
      error.message.includes('video') ||
      error.message.includes('microphone') ||
      error.message.includes('audio') ||
      error.message.includes('getUserMedia') ||
      error.message.includes('permission') ||
      error.message.includes('Permission') ||
      error.message.includes('denied') ||
      error.message.includes('device')
    );
  }
  return false;
};

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes('network') ||
      error.message.includes('connection') ||
      error.message.includes('offline') ||
      error.message.includes('internet') ||
      navigator.onLine === false
    );
  }
  return false;
};

export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes('permission') || 
      error.message.includes('denied') ||
      error.message.includes('NotAllowedError') ||
      error.name === 'NotAllowedError' ||
      error.name === 'PermissionDeniedError'
    );
  }
  return false;
};

export const isBrowserCompatibilityError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes('not supported') ||
      error.message.includes('not implemented') ||
      error.name === 'NotSupportedError'
    );
  }
  return false;
};

export default {
  handleError,
  isMediaDeviceError,
  isNetworkError,
  isPermissionError,
  isBrowserCompatibilityError
};
